/**
 * Safe JSON parsing for Play API clients (production + preview transports).
 */

export type SafeJsonResult<T> =
  | { ok: true; data: T; status: number }
  | {
      ok: false
      status: number
      code: string
      message: string
      rawSnippet?: string
    }

function isJsonContentType(contentType: string | null): boolean {
  if (!contentType) return false
  const lower = contentType.toLowerCase()
  return lower.includes('application/json') || lower.includes('+json')
}

function extractErrorFields(value: unknown): { code: string; message: string } | null {
  if (!value || typeof value !== 'object') return null
  const record = value as Record<string, unknown>
  if (record.error && typeof record.error === 'object') {
    const nested = record.error as Record<string, unknown>
    const code = typeof nested.code === 'string' ? nested.code : 'REQUEST_FAILED'
    const message =
      typeof nested.message === 'string'
        ? nested.message
        : typeof record.error === 'string'
          ? record.error
          : 'Unexpected response'
    return { code, message }
  }
  const code = typeof record.code === 'string' ? record.code : 'REQUEST_FAILED'
  const message =
    typeof record.error === 'string'
      ? record.error
      : typeof record.message === 'string'
        ? record.message
        : 'Unexpected response'
  return { code, message }
}

export async function readPlayApiJsonSafely<T>(
  response: Response,
  fallbackMessage = 'Request failed. Please try again.'
): Promise<SafeJsonResult<T>> {
  const status = response.status
  const contentType = response.headers.get('content-type')
  let text = ''

  try {
    text = await response.text()
  } catch {
    return {
      ok: false,
      status,
      code: 'NETWORK_READ_FAILED',
      message: fallbackMessage,
    }
  }

  if (!text.trim()) {
    return {
      ok: false,
      status,
      code: status >= 500 ? 'EMPTY_SERVER_RESPONSE' : 'EMPTY_RESPONSE',
      message: fallbackMessage,
    }
  }

  if (!isJsonContentType(contentType) && text.trimStart().startsWith('<')) {
    return {
      ok: false,
      status,
      code: 'HTML_RESPONSE',
      message: fallbackMessage,
      rawSnippet: text.slice(0, 120),
    }
  }

  try {
    const parsed = JSON.parse(text) as T
    if (!response.ok) {
      const err = extractErrorFields(parsed)
      return {
        ok: false,
        status,
        code: err?.code ?? 'REQUEST_FAILED',
        message: err?.message ?? fallbackMessage,
      }
    }
    return { ok: true, data: parsed, status }
  } catch {
    return {
      ok: false,
      status,
      code: 'MALFORMED_JSON',
      message: fallbackMessage,
      rawSnippet: text.slice(0, 120),
    }
  }
}
